/*
 * What a rendered message needs from the model to finish itself: the names and the pictures behind
 * the `@` mentions and the avatars, the groups behind the group chips, and — through chat's own
 * drawings (mentions.tsx) — the slug of every conversation a `#slug` or a permalink can point at.
 *
 * It is a projection and not a lookup: core's `MarkdownBody` renders hundreds of nodes and must not reach
 * into the store per node, so the three maps are built once per conversation view and handed down
 * through the render context.
 *
 * The identity of that context is load-bearing, and it is the one thing `memo()` cannot protect a
 * row from: a context change re-renders every component under the provider that reads it, and the
 * body of every message row is one of them. The projection reads three slices of the model, and only
 * one of them — the conversations — moves on every message, every read-state change and every member
 * change, while nothing a message renders comes out of it but the slugs. So each part is built by a
 * function that hands back the very same object while what it reads has not changed, `slugsOf`
 * comparing the slugs themselves rather than the slices they came from, and `useRenderContextValue`
 * assembles the context from the three: a message arriving in another conversation then changes
 * nothing this surface subscribes to, and a change in this conversation's unread count changes no
 * part of the context. Chat's drawings are built from the slugs and the surface's `navigate`, and
 * keep their identity for exactly as long as those two do.
 */

import { useMemo } from "react"
import { groupName } from "../../../core/groups/index.ts"
import type { RenderContextValue } from "../../../core/render/context.ts"
import type { Model } from "../../../core/store/model.ts"
import { useModel } from "../../../core/store/store.ts"
import { chatAddressing, type ChatAddressing } from "./mentions.tsx"

/** The slices the projection reads — a subset, so a caller may hand in its own selections. */
export type RenderContextModel = Pick<Model, "users" | "groups" | "conversations">

type People = Pick<RenderContextValue, "names" | "avatars">

/**
 * The last answer of each part, kept beside the model the way the sidebar's order is
 * (`state/selectors.ts`): one entry, because there is one model and the parts are read from it many
 * times per render.
 */
let peopleCache: { users: Model["users"]; value: People } | undefined
let groupCache: { groups: Model["groups"]; value: RenderContextValue["groups"] } | undefined
let slugCache: { conversations: Model["conversations"]; value: Record<number, string> } | undefined
let addressingCache:
	| {
			slugs: Record<number, string>
			navigate: ((path: string) => void) | undefined
			value: ChatAddressing
	  }
	| undefined

/** The names and the pictures, identical while `model.users` is. */
export function peopleOf(model: Pick<Model, "users">): People {
	if (peopleCache?.users === model.users) return peopleCache.value
	const names: Record<number, string> = {}
	// The picture is kept in its own map rather than beside the name, so a surface that only resolves
	// a mention carries nothing it does not render; an account without one is simply absent.
	const avatars: Record<number, string> = {}
	for (const [id, user] of Object.entries(model.users)) {
		names[Number(id)] = user.displayName
		if (user.avatarUrl !== undefined && user.avatarUrl !== "") avatars[Number(id)] = user.avatarUrl
	}
	const value: People = { names, avatars }
	peopleCache = { users: model.users, value }
	return value
}

/** The group chips, identical while `model.groups` is. */
export function groupsOf(model: Pick<Model, "groups">): RenderContextValue["groups"] {
	if (groupCache?.groups === model.groups) return groupCache.value
	const value: RenderContextValue["groups"] = {}
	for (const [id, group] of Object.entries(model.groups)) {
		value[Number(id)] = { displayName: groupName(group), memberIds: group.memberIds }
	}
	groupCache = { groups: model.groups, value }
	return value
}

/**
 * The address of every conversation a permalink can name, identical while no slug changed.
 *
 * This is the part that earns the comparison. `model.conversations` is replaced by every event that
 * touches any conversation — a message, an unread count, a member — and a slug changes when somebody
 * renames a channel, which is a handful of times a year. So the map is rebuilt and then *thrown away*
 * in favour of the one before it whenever the two say the same thing: the walk is one string read per
 * conversation, and what it saves is the render of every message body on the screen.
 */
export function slugsOf(model: Pick<Model, "conversations">): Record<number, string> {
	if (slugCache?.conversations === model.conversations) return slugCache.value
	const value: Record<number, string> = {}
	// A dm carries no slug; a permalink into one is addressed by its handle, not by a slug.
	for (const [id, slice] of Object.entries(model.conversations))
		value[Number(id)] = slice.meta.slug ?? ""
	const held = slugCache?.value
	const same =
		held !== undefined &&
		Object.keys(held).length === Object.keys(value).length &&
		Object.keys(value).every((id) => held[Number(id)] === value[Number(id)])
	const answer = same ? held : value
	slugCache = { conversations: model.conversations, value: answer }
	return answer
}

/** Forgets the three memos. For tests, which drive many models through one module. */
export function forgetRenderContextCaches(): void {
	peopleCache = undefined
	groupCache = undefined
	slugCache = undefined
	addressingCache = undefined
}

/** Chat's drawings for these slugs, identical while the slugs and the navigation are. */
function addressingOf(
	slugs: Record<number, string>,
	navigate: ((path: string) => void) | undefined,
): ChatAddressing {
	if (addressingCache?.slugs === slugs && addressingCache.navigate === navigate) {
		return addressingCache.value
	}
	const value = chatAddressing(slugs, navigate)
	addressingCache = { slugs, navigate, value }
	return value
}

export function buildRenderContext(
	model: RenderContextModel,
	navigate?: (path: string) => void,
	openProfile?: (userId: number) => void,
): RenderContextValue {
	// The two callers are optional and independent: a surface that cannot navigate still resolves
	// names, and a surface with nowhere to show a profile renders a mention as text (MarkdownBody).
	return {
		...peopleOf(model),
		groups: groupsOf(model),
		...addressingOf(slugsOf(model), navigate),
		...(navigate ? { navigate } : {}),
		...(openProfile ? { openProfile } : {}),
	}
}

/**
 * The context a surface hands to `RenderContextProvider`, subscribed part by part so that the value
 * keeps its identity for as long as nothing a message body draws has changed — which is what keeps a
 * message arriving in another conversation from re-rendering the three hundred rows of this one.
 *
 * Both callbacks belong to the surface and must already be stable (`useCallback`); they are part of
 * the value, so one rebuilt per render would undo everything above it.
 */
export function useRenderContextValue(
	navigate?: (path: string) => void,
	openProfile?: (userId: number) => void,
): RenderContextValue {
	const people = useModel(peopleOf)
	const groups = useModel(groupsOf)
	const slugs = useModel(slugsOf)
	return useMemo(
		() => ({
			...people,
			groups,
			...addressingOf(slugs, navigate),
			...(navigate ? { navigate } : {}),
			...(openProfile ? { openProfile } : {}),
		}),
		[people, groups, slugs, navigate, openProfile],
	)
}
